import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/services/logger-service';
import { toast } from 'sonner';

export type VideoCallStatus = 'idle' | 'calling' | 'connecting' | 'connected' | 'ended' | 'busy' | 'no_answer' | 'cancelled';

interface VideoCallState {
  callId: string | null;
  roomName: string | null;
  token: string | null;
  wsUrl: string | null;
  status: VideoCallStatus;
  error: string | null;
  callingRecipientName?: string;
  callingRecipientRole?: 'specialist' | 'caregiver';
}

interface IncomingCall {
  id: string;
  roomName: string;
  callerId: string;
  callerName: string;
  initiatedBy: 'specialist' | 'caregiver';
  isBotCall?: boolean;
  interviewId?: string;
  childId?: string;
}

interface VideoCallRecord {
  id: string;
  room_name: string;
  specialist_id: string;
  caregiver_id: string;
  status: string;
  initiated_by: string;
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
  is_bot_call?: boolean;
  interview_id?: string;
  child_id?: string;
}

// Check if a user is currently in an active call
async function checkUserBusy(userId: string, isSpecialist: boolean): Promise<{ busy: boolean; otherPartyName?: string }> {
  const column = isSpecialist ? 'specialist_id' : 'caregiver_id';
  
  // Get the user's ID in the video_calls table
  let tableUserId: string | null = null;
  
  if (isSpecialist) {
    const { data } = await supabase
      .from('specialists')
      .select('id')
      .eq('auth_user_id', userId)
      .single();
    tableUserId = data?.id || null;
  } else {
    const { data } = await supabase
      .from('caregivers')
      .select('id')
      .eq('auth_user_id', userId)
      .single();
    tableUserId = data?.id || null;
  }
  
  if (!tableUserId) return { busy: false };
  
  const { data: activeCalls } = await supabase
    .from('video_calls' as any)
    .select('*')
    .eq(column, tableUserId)
    .in('status', ['pending', 'active']);
  
  if (activeCalls && activeCalls.length > 0) {
    // Get the other party's name
    const call = activeCalls[0] as unknown as VideoCallRecord;
    const otherColumn = isSpecialist ? 'caregiver_id' : 'specialist_id';
    const otherTable = isSpecialist ? 'caregivers' : 'specialists';
    
    const { data: otherParty } = await supabase
      .from(otherTable)
      .select('name')
      .eq('id', call[otherColumn as keyof VideoCallRecord] as string)
      .single();
    
    return { busy: true, otherPartyName: otherParty?.name || 'someone' };
  }
  
  return { busy: false };
}

// Check if the target user is busy
// Only consider non-bot calls from the last hour as "active" - older ones are stale
async function checkTargetBusy(targetId: string, isTargetSpecialist: boolean): Promise<{ busy: boolean; otherPartyName?: string }> {
  const column = isTargetSpecialist ? 'specialist_id' : 'caregiver_id';
  
  // Only check calls created in the last hour to avoid stale records
  const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
  
  const { data: activeCalls } = await supabase
    .from('video_calls' as any)
    .select('*')
    .eq(column, targetId)
    .in('status', ['pending', 'active'])
    .gte('created_at', oneHourAgo);
  
  if (activeCalls && activeCalls.length > 0) {
    // Filter out bot calls - they don't block real calls
    const realCalls = activeCalls.filter((call: any) => !call.is_bot_call);
    
    if (realCalls.length > 0) {
      const call = realCalls[0] as unknown as VideoCallRecord;
      const otherColumn = isTargetSpecialist ? 'caregiver_id' : 'specialist_id';
      const otherTable = isTargetSpecialist ? 'caregivers' : 'specialists';
      
      const { data: otherParty } = await supabase
        .from(otherTable)
        .select('name')
        .eq('id', call[otherColumn as keyof VideoCallRecord] as string)
        .single();
      
      return { busy: true, otherPartyName: otherParty?.name || 'someone' };
    }
  }
  
  return { busy: false };
}

export function useVideoCall(userId?: string, isSpecialist: boolean = false) {
  const [state, setState] = useState<VideoCallState>({
    callId: null,
    roomName: null,
    token: null,
    wsUrl: null,
    status: 'idle',
    error: null,
    callingRecipientName: undefined,
    callingRecipientRole: undefined,
  });

  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);
  const callTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (callTimeoutRef.current) {
        clearTimeout(callTimeoutRef.current);
      }
    };
  }, []);

  // Generate unique room name
  const generateRoomName = (specialistId: string, caregiverId: string): string => {
    return `sleepnest-${specialistId.slice(0, 8)}-${caregiverId.slice(0, 8)}-${Date.now()}`;
  };

  // Start a video call (can be initiated by either party)
  const startCall = useCallback(async (
    specialistId: string,
    caregiverId: string,
    callerName: string,
    initiatedBy: 'specialist' | 'caregiver' = 'specialist',
    recipientName?: string
  ) => {
    try {
      // Store recipient info for calling screen
      const targetRole = initiatedBy === 'specialist' ? 'caregiver' : 'specialist';
      setState(prev => ({ 
        ...prev, 
        status: 'calling', 
        error: null,
        callingRecipientName: recipientName,
        callingRecipientRole: targetRole as 'specialist' | 'caregiver',
      }));
      logger.info('Starting video call', { specialistId, caregiverId, initiatedBy });

      // Check if caller is already in a call
      const callerBusy = await checkUserBusy(userId!, isSpecialist);
      if (callerBusy.busy) {
        setState(prev => ({ ...prev, status: 'idle', error: 'You are already in a call' }));
        toast.error(`You are already in a call with ${callerBusy.otherPartyName}`);
        throw new Error('You are already in a call');
      }

      // Check if target is busy
      const targetId = initiatedBy === 'specialist' ? caregiverId : specialistId;
      const isTargetSpecialist = initiatedBy === 'caregiver';
      const targetBusy = await checkTargetBusy(targetId, isTargetSpecialist);
      
      if (targetBusy.busy) {
        setState(prev => ({ ...prev, status: 'busy', error: 'The person you are calling is busy' }));
        toast.error(`Line busy - they are currently in a call with ${targetBusy.otherPartyName}`, {
          description: 'Please try again later.',
          duration: 5000,
        });
        throw new Error('Target is busy');
      }

      const roomName = generateRoomName(specialistId, caregiverId);

      // Create video call record in database
      const { data: callData, error: dbError } = await supabase
        .from('video_calls' as any)
        .insert({
          room_name: roomName,
          specialist_id: specialistId,
          caregiver_id: caregiverId,
          status: 'pending',
          initiated_by: initiatedBy,
        })
        .select()
        .single();

      if (dbError) {
        throw new Error(`Failed to create call record: ${dbError.message}`);
      }

      const call = callData as unknown as VideoCallRecord;

      // Get token from edge function
      const { data: tokenData, error: tokenError } = await supabase.functions.invoke(
        'generate-video-token',
        {
          body: {
            roomName,
            participantName: callerName,
            participantIdentity: initiatedBy === 'specialist' ? specialistId : caregiverId,
            isHost: true,
          },
        }
      );

      if (tokenError || !tokenData?.token) {
        throw new Error(tokenError?.message || 'Failed to generate token');
      }

      setState(prev => ({
        ...prev,
        callId: call.id,
        roomName,
        token: tokenData.token,
        wsUrl: tokenData.wsUrl,
        status: 'calling', // Keep as 'calling' until recipient answers
      }));

      logger.info('Video call started, waiting for recipient', { callId: call.id, roomName, initiatedBy });
      return { callId: call.id, roomName };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start call';
      logger.error('Failed to start video call', { error });
      if (state.status !== 'busy') {
        setState(prev => ({ ...prev, status: 'idle', error: message }));
      }
      throw error;
    }
  }, [userId, isSpecialist]);

  // Join an existing call (receiver accepts)
  const joinCall = useCallback(async (
    callId: string,
    roomName: string,
    participantId: string,
    participantName: string
  ) => {
    try {
      setState(prev => ({ ...prev, status: 'connecting', error: null }));
      logger.info('Joining video call', { callId, roomName });

      // Update call status to active
      const { error: updateError } = await supabase
        .from('video_calls' as any)
        .update({ 
          status: 'active',
          started_at: new Date().toISOString()
        })
        .eq('id', callId);

      if (updateError) {
        logger.error('Failed to update call status', { error: updateError });
      }

      // Get token from edge function
      const { data: tokenData, error: tokenError } = await supabase.functions.invoke(
        'generate-video-token',
        {
          body: {
            roomName,
            participantName: participantName,
            participantIdentity: participantId,
            isHost: false,
          },
        }
      );

      if (tokenError || !tokenData?.token) {
        throw new Error(tokenError?.message || 'Failed to generate token');
      }

      setState(prev => ({
        ...prev,
        callId,
        roomName,
        token: tokenData.token,
        wsUrl: tokenData.wsUrl,
        status: 'connected',
      }));

      setIncomingCall(null);
      logger.info('Joined video call', { callId, roomName });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to join call';
      logger.error('Failed to join video call', { error });
      setState(prev => ({ ...prev, status: 'idle', error: message }));
      toast.error(message);
      throw error;
    }
  }, []);

  // End the current call
  const endCall = useCallback(async () => {
    try {
      if (state.callId) {
        const { error } = await supabase
          .from('video_calls' as any)
          .update({ 
            status: 'completed',
            ended_at: new Date().toISOString()
          })
          .eq('id', state.callId);

        if (error) {
          logger.error('Failed to update call end status', { error });
        }
      }

      logger.info('Video call ended', { callId: state.callId });
      setState({
        callId: null,
        roomName: null,
        token: null,
        wsUrl: null,
        status: 'ended',
        error: null,
      });

      // Reset to idle after a brief moment
      setTimeout(() => {
        setState(prev => ({ ...prev, status: 'idle' }));
      }, 1000);
    } catch (error) {
      logger.error('Error ending call', { error });
    }
  }, [state.callId]);

  // Decline an incoming call
  const declineCall = useCallback(async () => {
    if (incomingCall) {
      try {
        await supabase
          .from('video_calls' as any)
          .update({ status: 'cancelled' })
          .eq('id', incomingCall.id);
      } catch (error) {
        logger.error('Error declining call', { error });
      }
      setIncomingCall(null);
    }
  }, [incomingCall]);

  // Cancel an outgoing call (timeout or user cancellation)
  const cancelCall = useCallback(async (reason: 'timeout' | 'cancelled' = 'cancelled') => {
    try {
      if (callTimeoutRef.current) {
        clearTimeout(callTimeoutRef.current);
        callTimeoutRef.current = null;
      }

      if (state.callId) {
        const status = reason === 'timeout' ? 'no_answer' : 'cancelled';
        await supabase
          .from('video_calls' as any)
          .update({ status })
          .eq('id', state.callId);
        
        if (reason === 'timeout') {
          toast.info('No answer', { description: 'The call was not answered.' });
        }
      }

      logger.info('Call cancelled', { callId: state.callId, reason });
      setState({
        callId: null,
        roomName: null,
        token: null,
        wsUrl: null,
        status: 'idle',
        error: null,
        callingRecipientName: undefined,
        callingRecipientRole: undefined,
      });
    } catch (error) {
      logger.error('Error cancelling call', { error });
      setState(prev => ({ ...prev, status: 'idle' }));
    }
  }, [state.callId]);

  // Set call as connected (when LiveKit connects)
  const setConnected = useCallback(() => {
    if (callTimeoutRef.current) {
      clearTimeout(callTimeoutRef.current);
      callTimeoutRef.current = null;
    }
    setState(prev => ({ ...prev, status: 'connected' }));
  }, []);

  // Listen for incoming calls (works for both caregivers and specialists)
  useEffect(() => {
    if (!userId) {
      logger.debug('Skipping incoming call subscription - no userId');
      return;
    }

    logger.info('Setting up incoming call subscription', { userId, isSpecialist });

    const setupSubscription = async () => {
      // Get the user's ID in the relevant table
      let tableUserId: string | null = null;
      
      if (isSpecialist) {
        const { data: specialist, error } = await supabase
          .from('specialists')
          .select('id')
          .eq('auth_user_id', userId)
          .single();

        if (error || !specialist) {
          logger.error('Failed to get specialist ID for video call subscription', { error, userId });
          return;
        }
        tableUserId = specialist.id;
      } else {
        const { data: caregiver, error } = await supabase
          .from('caregivers')
          .select('id')
          .eq('auth_user_id', userId)
          .single();

        if (error || !caregiver) {
          logger.error('Failed to get caregiver ID for video call subscription', { error, userId });
          return;
        }
        tableUserId = caregiver.id;
      }

      logger.info('Subscribing to video calls', { tableUserId, isSpecialist });

      // Subscribe to calls where this user is the recipient
      const filterColumn = isSpecialist ? 'specialist_id' : 'caregiver_id';
      
      const channel = supabase
        .channel(`video-calls-incoming-${tableUserId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'video_calls',
            filter: `${filterColumn}=eq.${tableUserId}`,
          },
          async (payload) => {
            logger.info('Video call INSERT event received', { payload });
            const call = payload.new as VideoCallRecord;
            
            // Only show notification if we're the recipient (not the initiator)
            const isRecipient = (isSpecialist && call.initiated_by === 'caregiver') ||
                               (!isSpecialist && call.initiated_by === 'specialist');
            
            if (call.status === 'pending' && isRecipient) {
              // Fetch caller name
              const callerTable = call.initiated_by === 'specialist' ? 'specialists' : 'caregivers';
              const callerId = call.initiated_by === 'specialist' ? call.specialist_id : call.caregiver_id;
              
              const { data: caller } = await supabase
                .from(callerTable)
                .select('name')
                .eq('id', callerId)
                .single();

              logger.info('Setting incoming call state', { 
                callId: call.id, 
                roomName: call.room_name,
                callerName: caller?.name,
                initiatedBy: call.initiated_by
              });

              setIncomingCall({
                id: call.id,
                roomName: call.room_name,
                callerId: callerId,
                callerName: call.is_bot_call ? 'AI Sleep Coach' : (caller?.name || (call.initiated_by === 'specialist' ? 'Sleep Coach' : 'Caregiver')),
                initiatedBy: call.initiated_by as 'specialist' | 'caregiver',
                isBotCall: call.is_bot_call || false,
                interviewId: call.interview_id,
                childId: call.child_id,
              });

              logger.info('Incoming video call', { callId: call.id });
            }
          }
        )
        .subscribe((status) => {
          logger.info('Video call channel subscription status', { status });
        });

      return () => {
        logger.info('Cleaning up video call subscription');
        supabase.removeChannel(channel);
      };
    };

    setupSubscription();
  }, [userId, isSpecialist]);

  // Listen for call status changes
  useEffect(() => {
    if (!state.callId) return;

    const channel = supabase
      .channel(`video-call-${state.callId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'video_calls',
          filter: `id=eq.${state.callId}`,
        },
        (payload) => {
          const call = payload.new as VideoCallRecord;
          if (call.status === 'active' && state.status === 'calling') {
            setState(prev => ({ ...prev, status: 'connected' }));
            toast.success('Call connected!');
          } else if (call.status === 'cancelled') {
            // Recipient declined the call - show 'cancelled' status to caller
            // so they get feedback that the call was declined
            if (state.status === 'calling') {
              setState(prev => ({ ...prev, status: 'cancelled' }));
              // Don't auto-reset - let the UI handle showing the declined message
            } else if (state.status !== 'ended') {
              toast.info('Call ended');
              endCall();
            }
          } else if (call.status === 'completed') {
            if (state.status !== 'ended') {
              toast.info('Call ended');
              endCall();
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [state.callId, state.status, endCall]);

  return {
    ...state,
    incomingCall,
    startCall,
    joinCall,
    endCall,
    declineCall,
    cancelCall,
    setConnected,
  };
}
